<template>
  <div class="my-1">
    <p class="lead mb-1" style="white-space: pre-wrap" v-html="nodeData.question"></p>
    <validation-provider
      :name="nodeData.question"
      :rules="nodeData.rules"
      ref="formObserver"
      v-slot="{ errors }"
    >
      <template v-if="isCheckboxField">
        <b-form-checkbox-group
          v-model="action.data.answer"
          name="checkbox-buttons"
          :disabled="terminated || validating || action.data.continue"
          stacked
        >
          <b-form-checkbox
            v-for="(option, index) in nodeData.options"
            :key="index"
            :value="option"
          >
            {{ option }}
          </b-form-checkbox>
        </b-form-checkbox-group>
      </template>
      <template v-else-if="isInputField">
        <input
          type="text"
          class="form-control"
          v-model="action.data.answer"
          :disabled="terminated || validating || action.data.continue"
        />
      </template>
      <template v-else-if="isSelectField">
        <v-select
          :options="nodeData.options"
          input-id="field-type"
          placeholder="Seleccionar"
          append-to-body
          v-model="action.data.answer"
          :calculate-position="withPopper"
          :disabled="terminated || validating || action.data.continue"
        />
      </template>
      <template v-else-if="isTextAreaField">
        <b-form-textarea
          rows="3"
          max-rows="6"
          v-model="action.data.answer"
          :disabled="isCommentEditingDisabled"
        ></b-form-textarea>
      </template>
      <p v-if="errors" class="small text-danger mt-1">
        {{ errors[0] }}
      </p>
    </validation-provider>

    <template v-if="isTextAreaField">
      <b-button
        class="my-1 float-right"
        variant="primary"
        size="sm"
        :disabled="isCommentEditingDisabled"
        @click="save()"
      >
        Guardar
      </b-button>
      <feather-icon
        v-if="showSaveSuccess"
        class="float-right my-2 mr-1 text-success"
        icon="CheckCircleIcon"
        size="20"
        title="Información guardada correctamente"
        aria-label="Información guardada correctamente"
      />
    </template>

    <b-button
      class="my-1"
      variant="primary"
      size="sm"
      :disabled="terminated || validating || action.data.continue"
      @click="onContinueClick(action)"
    >
      {{ $t("action_plan.viewer.continue") }}
      <feather-icon icon="ChevronRightIcon" size="18"/>
    </b-button>
  </div>
</template>

<script>
import vSelect from "vue-select";
import moment from "moment-timezone";
import popperHelper from '@/libs/popper-helper'
import {ValidationProvider, extend} from "vee-validate";

const COMMENT_EDIT_WINDOW_SECONDS = 120;

extend('length', (value, {length}) => {
  if (value.length >= length) return true;
  return `El campo {_field_} debe tener al menos ${length} opciones seleccionadas`;
});

export default {
  props: {
    actionPlanLogUuid: String,
    action: Object,
    terminated: Boolean,
    preview: Boolean,
    validating: Boolean,
  },
  components: {
    ValidationProvider,
    vSelect
  },
  data() {
    return {
      withPopper: popperHelper.withPopper,
      formValidated: false,
      showSaveSuccess: false,
      saveSuccessTimeout: null,
      commentEditWindowInterval: null,
      currentTime: Date.now(),
    }
  },
  mounted() {
    setTimeout(async () => await this.$refs.formObserver.validate(), 50);
    this.startCommentEditWindowClock();
  },
  beforeDestroy() {
    clearTimeout(this.saveSuccessTimeout);
    this.stopCommentEditWindowClock();
  },
  computed: {
    isCheckboxField() {
      return (this.nodeData.type === 'Checkbox');
    },
    isInputField() {
      return (this.nodeData.type === 'Input');
    },
    isSelectField() {
      return (this.nodeData.type === 'Select');
    },
    isTextAreaField() {
      return (this.nodeData.type === 'Textarea');
    },
    nodeData() {
      return this.action.node_data;
    },
    hasUnlimitedCommentEditingPermission() {
      return typeof this.$can === "function"
        && this.$can("allow_edit_comments_with_no_time_limit", "ActionPlan");
    },
    commentEditWindowExpiresAt() {
      const startedAt = this.action?.data?.comment_edit_started_at
        || this.action?.data?.timestamp;
      if (!startedAt) return null;

      const commentEditStartedAt = moment.tz(
        startedAt,
        "YYYY-MM-DD HH:mm:ss",
        "America/Mexico_City",
      );
      if (!commentEditStartedAt.isValid()) return null;

      return commentEditStartedAt.add(COMMENT_EDIT_WINDOW_SECONDS, "seconds").valueOf();
    },
    canEditComment() {
      if (this.hasUnlimitedCommentEditingPermission) {
        return true;
      }

      if (!this.action?.data?.continue && !this.action?.data?.comment_edit_started_at) {
        return true;
      }

      return this.commentEditWindowExpiresAt !== null
        && this.currentTime < this.commentEditWindowExpiresAt;
    },
    isCommentEditingDisabled() {
      return this.terminated || this.validating || !this.canEditComment;
    }
  },
  watch: {
    "action.data.continue"() {
      this.startCommentEditWindowClock();
    },
    "action.data.comment_edit_started_at"() {
      this.startCommentEditWindowClock();
    },
  },
  methods: {
    async onContinueClick(action) {
      const validation = await this.$refs.formObserver.validate();

      if (!validation.valid) {
        return
      }

      this.startCommentEditWindow();
      this.$emit("continue", action);
    },
    save() {
      if (this.isCommentEditingDisabled) return;

      this.startCommentEditWindow();
      this.$emit("checkpoint", this.displaySaveSuccess);
    },
    startCommentEditWindow() {
      if (this.action?.data?.comment_edit_started_at) return;

      this.$set(
        this.action.data,
        "comment_edit_started_at",
        moment.tz("America/Mexico_City").format("YYYY-MM-DD HH:mm:ss"),
      );
    },
    startCommentEditWindowClock() {
      this.stopCommentEditWindowClock();
      this.refreshCommentEditWindow();

      if (
        this.canEditComment
        && this.action?.data?.comment_edit_started_at
        && !this.hasUnlimitedCommentEditingPermission
      ) {
        this.commentEditWindowInterval = setInterval(
          () => this.refreshCommentEditWindow(),
          1000,
        );
      }
    },
    stopCommentEditWindowClock() {
      clearInterval(this.commentEditWindowInterval);
      this.commentEditWindowInterval = null;
    },
    refreshCommentEditWindow() {
      this.currentTime = Date.now();

      if (!this.canEditComment) {
        this.stopCommentEditWindowClock();
      }
    },
    displaySaveSuccess() {
      clearTimeout(this.saveSuccessTimeout);
      this.showSaveSuccess = true;
      this.saveSuccessTimeout = setTimeout(() => {
        this.showSaveSuccess = false;
      }, 3000);
    }
  }
}
</script>
